import { getFreshBearerToken, recoverAuthAfter401 } from "@healthos/auth/react";

let installed = false;

// The Prybar SDK calls global fetch and only supports cookie auth; HealthOS uses
// OIDC bearer tokens. Install a scoped wrapper that injects a fresh bearer for
// Prybar requests only, reusing the same refresh-aware token the RPC client uses.
// At startup the OIDC session is still loading, so getFreshBearerToken briefly
// returns null. Sending a Prybar request then 401s (the capabilities probe fires
// early), recovers, and leaves a red error in the console. Wait a short beat for
// the token so the first request carries auth instead of racing auth.
async function tokenWhenReady(maxWaitMs = 4000): Promise<string | null> {
  let token = await getFreshBearerToken();
  const deadline = Date.now() + maxWaitMs;
  while (!token && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    token = await getFreshBearerToken();
  }
  return token;
}

export function installPrybarFetchAuth(prefix: string): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const realFetch = window.fetch.bind(window);
  // We authenticate by bearer header, not cookies. The SDK defaults to
  // credentials:"include", which a cross-origin call against CORS "*" rejects.
  const send = (input: RequestInfo | URL, init: RequestInit | undefined, token: string | null) => {
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
    return realFetch(input, { ...init, headers, credentials: "omit" });
  };
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (!url.includes(prefix)) return realFetch(input, init);
    const res = await send(input, init, await tokenWhenReady());
    if (res.status !== 401) return res;
    // The token lapsed and silent renew lost the race; the SDK has no 401
    // recovery of its own, so recover once here the way authedFetch does.
    const refreshed = await recoverAuthAfter401();
    return refreshed ? send(input, init, refreshed) : res;
  };
}
